import { FC, useMemo } from 'react';
import {
  Checkbox,
  Dropdown,
  FilterableMultiSelect,
  NumberInput,
  TextInput,
} from '@carbon/react';
import { useDebouncedChange } from 'components/debounced-input';
import {
  FilterSection,
  ColumnDateFilter,
  useDataTableContext,
} from 'components/data-table';
import type { TypeCurvesListingFacets } from './use-type-curves-listing';
import styles from './type-curves-listing-filters.module.scss';

// Filter key constants — used in columnFilters state and translated
// to API params by the parent (type-curves-content.tsx).
export const LISTING_FILTER_IDS = {
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  archived: 'archived',
  hasNewWells: 'hasNewWells',
  typeCurveScenario: 'typeCurveScenario',
  basin: 'basin',
  typeCurveAreaName: 'typeCurveAreaName',
  formation: 'formation',
  dataLoadSource: 'dataLoadSource',
  status: 'status',
  createdBy: 'createdBy',
  developmentType: 'developmentType',
  basinAreaCount: 'basinAreaCount',
  dcaScenario: 'dcaScenario',
  scalingVariables: 'scalingVariables',
  promoted: 'promoted',
  promotedBy: 'promotedBy',
  tasksComplete: 'tasksComplete',
  isDefault: 'isDefault',
  verification: 'verification',
  validation: 'validation',
  updatedBy: 'updatedBy',
  uniqueTcId: 'uniqueTcId',
} as const;

type FilterId = (typeof LISTING_FILTER_IDS)[keyof typeof LISTING_FILTER_IDS];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SCALING_VARIABLE_OPTIONS = [
  { value: 'perforated_lateral_length_ft', label: 'Perforated Lateral Length' },
  {
    value: 'horizontal_well_spacing_in_zone_ft',
    label: 'Well Spacing in Zone',
  },
  { value: 'proppant_intensity_lbs_ft', label: 'Proppant Intensity' },
  { value: 'fluid_intensity_bbls_ft', label: 'Fluid Intensity' },
  { value: 'eur_oil_bbl_per_ft', label: 'Oil EUR per ft' },
  { value: 'eur_gas_mcf_per_ft', label: 'Gas EUR per ft' },
  { value: 'eur_water_bbl_per_ft', label: 'Water EUR per ft' },
];

type DropdownItem = { id: string; label: string };
type ScalingItem = { value: string; label: string };

const toItems = (
  buckets: { value: string; count: number }[] | undefined
): DropdownItem[] => [
  { id: '', label: 'Select' },
  ...(buckets ?? []).map((b) => ({
    id: b.value,
    label: b.value,
  })),
];

// Scenario facet buckets label by name but filter by id: id = value (the id we
// send), label = the display name.
const toScenarioItems = (
  buckets: { value: string; label: string; count: number }[] | undefined
): DropdownItem[] => [
  { id: '', label: 'Select' },
  ...(buckets ?? []).map((b) => ({
    id: b.value,
    label: b.label,
  })),
];

// Fixed options (not facet-driven). Values match the backend `has_new_wells` enum.
const HAS_NEW_WELLS_ITEMS: DropdownItem[] = [
  { id: '', label: 'Select' },
  // Labels are Yes/No; ids remain the backend enum values.
  { id: 'since_created', label: 'Yes' }, // Yes -> new wells since first created
  { id: 'since_last_qc', label: 'No' }, // No -> new wells since last QC
];

const VERIFICATION_ITEMS: DropdownItem[] = [
  { id: '', label: 'Select' },
  { id: 'correct', label: 'Correct' },
  { id: 'incorrect', label: 'Incorrect' },
];

const VALIDATION_ITEMS: DropdownItem[] = [
  { id: '', label: 'Select' },
  { id: 'valid', label: 'Valid' },
  { id: 'warning', label: 'Warning' },
  { id: 'invalid', label: 'Invalid' },
  { id: 'incomplete', label: 'Incomplete' },
];

// ─── Filter field hooks (read/write from DataTable context directly) ──────────

function useFilterValue(id: FilterId): unknown {
  const { columnFilters } = useDataTableContext();
  return columnFilters.find((f) => f.id === id)?.value;
}

function useSetFilterValue(id: FilterId) {
  const { columnFilters, setColumnFilters } = useDataTableContext();
  return (value: unknown) => {
    const others = columnFilters.filter((f) => f.id !== id);
    if (value === undefined || value === null || value === '') {
      setColumnFilters(others);
    } else {
      setColumnFilters([...others, { id, value }]);
    }
  };
}

// ─── Individual filter components (each uses context directly) ─────────────────

const SelectFilter: FC<{
  id: FilterId;
  label: string;
  placeholder: string;
  items: DropdownItem[];
}> = ({ id, label, placeholder, items }) => {
  const value = useFilterValue(id) as string | undefined;
  const setValue = useSetFilterValue(id);
  return (
    <Dropdown
      id={`tc-filter-${id}`}
      titleText={label}
      label={placeholder}
      items={items}
      itemToString={(item: DropdownItem | null) => item?.label ?? ''}
      selectedItem={items.find((i) => i.id === (value ?? '')) ?? items[0]}
      onChange={({ selectedItem }: { selectedItem: DropdownItem | null }) =>
        setValue(selectedItem?.id || undefined)
      }
      size="sm"
    />
  );
};

const BoolFilter: FC<{
  id: FilterId;
  label: string;
  trueLabel?: string;
  falseLabel?: string;
}> = ({ id, label, trueLabel = 'Yes', falseLabel = 'No' }) => {
  const ITEMS: DropdownItem[] = [
    { id: '', label: 'Select' },
    { id: 'true', label: trueLabel },
    { id: 'false', label: falseLabel },
  ];
  return (
    <SelectFilter id={id} label={label} placeholder="Select" items={ITEMS} />
  );
};

// If unchecked (default), show active Type Curves only. If checked, show all.
const ArchivedFilter: FC = () => {
  const value = useFilterValue(LISTING_FILTER_IDS.archived) as
    boolean | undefined;
  const setValue = useSetFilterValue(LISTING_FILTER_IDS.archived);
  const checked = value === true;
  return (
    <Checkbox
      id="tc-filter-archived"
      labelText="Archived"
      checked={checked}
      onChange={(_: unknown, { checked: next }: { checked: boolean }) =>
        setValue(next ? true : undefined)
      }
    />
  );
};

interface TextFilterProps {
  id: FilterId;
  label: string;
  placeholder: string;
}

/**
 * Free text, sent once typing pauses so a keystroke is not a query.
 * @param {TextFilterProps} props - The filter id, label and placeholder.
 * @return {JSX.Element} The input.
 */
function TextFilter({ id, label, placeholder }: TextFilterProps) {
  const value = (useFilterValue(id) as string | undefined) ?? '';
  const setValue = useSetFilterValue(id);
  const [local, change] = useDebouncedChange(value, (next: string) =>
    setValue(next.trim() || undefined)
  );
  return (
    <TextInput
      id={`tc-filter-${id}`}
      labelText={label}
      placeholder={placeholder}
      size="sm"
      value={local}
      onChange={(event) => change(event.target.value)}
    />
  );
}

const RangeFilter: FC<{ id: FilterId; label: string }> = ({ id, label }) => {
  const value = useFilterValue(id) as
    { min?: number; max?: number } | undefined;
  const setValue = useSetFilterValue(id);
  return (
    <>
      <p className={styles.rangeLabel}>{label}</p>
      <div className={styles.rangeRow}>
        <NumberInput
          id={`tc-filter-${id}-min`}
          label=""
          placeholder="Min"
          size="sm"
          allowEmpty
          hideSteppers
          value={value?.min ?? ''}
          onChange={(_: unknown, { value: v }: { value: string | number }) => {
            const newMin = v !== '' && v !== undefined ? Number(v) : undefined;
            const newMax = value?.max;
            setValue(
              newMin === undefined && newMax === undefined
                ? undefined
                : { min: newMin, max: newMax }
            );
          }}
        />
        <NumberInput
          id={`tc-filter-${id}-max`}
          label=""
          placeholder="Max"
          size="sm"
          allowEmpty
          hideSteppers
          value={value?.max ?? ''}
          onChange={(_: unknown, { value: v }: { value: string | number }) => {
            const newMax = v !== '' && v !== undefined ? Number(v) : undefined;
            const newMin = value?.min;
            setValue(
              newMin === undefined && newMax === undefined
                ? undefined
                : { min: newMin, max: newMax }
            );
          }}
        />
      </div>
    </>
  );
};

const ScalingVariablesFilter: FC = () => {
  const { columnFilters, setColumnFilters } = useDataTableContext();
  const current = columnFilters.find(
    (f) => f.id === LISTING_FILTER_IDS.scalingVariables
  );
  const selected = (current?.value as string[] | undefined) ?? [];
  const selectedItems = SCALING_VARIABLE_OPTIONS.filter((o) =>
    selected.includes(o.value)
  );

  const handleChange = (next: string[]) => {
    const others = columnFilters.filter(
      (f) => f.id !== LISTING_FILTER_IDS.scalingVariables
    );
    setColumnFilters(
      next.length > 0
        ? [...others, { id: LISTING_FILTER_IDS.scalingVariables, value: next }]
        : others
    );
  };

  return (
    <FilterableMultiSelect
      key={`scaling-${selected.length}`}
      id="tc-filter-scaling-variables"
      titleText="Scaling Variables"
      placeholder={
        selected.length === 0
          ? 'Select variables'
          : selected.length === 1
            ? (selectedItems[0]?.label ?? 'Selected')
            : 'Selected Scaling Variables'
      }
      items={SCALING_VARIABLE_OPTIONS}
      initialSelectedItems={selectedItems}
      itemToString={(item: ScalingItem | null) => item?.label ?? ''}
      onChange={({
        selectedItems,
      }: {
        selectedItems: readonly ScalingItem[];
      }) => handleChange(selectedItems.map((i) => i.value))}
    />
  );
};

// ─── Main filter panel ────────────────────────────────────────────────────────

interface TypeCurvesListingFiltersProps {
  facets: TypeCurvesListingFacets | undefined;
  /**
   * Shows the Type Curve ID filter. Only the Inventories view answers it, so
   * the other views leave it out rather than offer a control that does nothing.
   */
  showTypeCurveId?: boolean;
}

export const TypeCurvesListingFilters: FC<TypeCurvesListingFiltersProps> = ({
  facets,
  showTypeCurveId = false,
}) => {
  const basinItems = useMemo(
    () => toItems(facets?.basin?.buckets),
    [facets?.basin]
  );
  const formationItems = useMemo(
    () => toItems(facets?.formation?.buckets),
    [facets?.formation]
  );
  const tcAreaItems = useMemo(
    () => toItems(facets?.typeCurveAreaName?.buckets),
    [facets?.typeCurveAreaName]
  );
  const dataSourceItems = useMemo(
    () => toItems(facets?.dataLoadSource?.buckets),
    [facets?.dataLoadSource]
  );
  const tcScenarioItems = useMemo(
    () => toScenarioItems(facets?.typeCurveScenario?.buckets),
    [facets?.typeCurveScenario]
  );
  const createdByItems = useMemo(
    () => toItems(facets?.createdBy?.buckets),
    [facets?.createdBy]
  );
  const dcaScenarioItems = useMemo(
    () => toItems(facets?.dcaScenario?.buckets),
    [facets?.dcaScenario]
  );
  const promotedByItems = useMemo<DropdownItem[]>(
    () => [
      { id: '', label: 'Select' },
      ...(facets?.promotedBy?.buckets ?? []).map((b) => ({
        id: b.value,
        label: b.value,
      })),
    ],
    [facets?.promotedBy]
  );

  const updatedByItems = useMemo<DropdownItem[]>(
    () => [
      { id: '', label: 'Select' },
      ...(facets?.updatedBy?.buckets ?? []).map((b) => ({
        id: b.value,
        label: b.value,
      })),
    ],
    [facets?.updatedBy]
  );

  const DEV_TYPE_ITEMS: DropdownItem[] = [
    { id: '', label: 'Select type' },
    { id: 'HORIZONTAL', label: 'Horizontal' },
    { id: 'VERTICAL', label: 'Vertical' },
  ];

  return (
    <div className={styles.wrapper}>
      <FilterSection title="Date Filter">
        <ColumnDateFilter
          columnId={LISTING_FILTER_IDS.createdAt}
          label="Created At"
        />
        <ColumnDateFilter
          columnId={LISTING_FILTER_IDS.updatedAt}
          label="Updated At"
        />
      </FilterSection>

      <FilterSection title="Type Curve Filters">
        <ArchivedFilter />
        <SelectFilter
          id={LISTING_FILTER_IDS.typeCurveScenario}
          label="Type Curve Scenario"
          placeholder="Select"
          items={tcScenarioItems}
        />
        <SelectFilter
          id={LISTING_FILTER_IDS.hasNewWells}
          label="Has New Wells"
          placeholder="Select"
          items={HAS_NEW_WELLS_ITEMS}
        />
        <SelectFilter
          id={LISTING_FILTER_IDS.basin}
          label="Basin"
          placeholder="Select"
          items={basinItems}
        />
        <SelectFilter
          id={LISTING_FILTER_IDS.typeCurveAreaName}
          label="Type Curve Area"
          placeholder="Select"
          items={tcAreaItems}
        />
        <SelectFilter
          id={LISTING_FILTER_IDS.formation}
          label="Formation"
          placeholder="Select"
          items={formationItems}
        />
        <SelectFilter
          id={LISTING_FILTER_IDS.dataLoadSource}
          label="Data Source"
          placeholder="Select"
          items={dataSourceItems}
        />
      </FilterSection>

      <FilterSection title="Type Curve Scenario Details">
        <SelectFilter
          id={LISTING_FILTER_IDS.developmentType}
          label="Development Type"
          placeholder="Select type"
          items={DEV_TYPE_ITEMS}
        />
        <RangeFilter
          id={LISTING_FILTER_IDS.basinAreaCount}
          label="Basin Area Count"
        />
        <SelectFilter
          id={LISTING_FILTER_IDS.dcaScenario}
          label="DCA Scenario"
          placeholder="Select scenario"
          items={dcaScenarioItems}
        />
        <ScalingVariablesFilter />
      </FilterSection>

      {/* The listing mock splits the workspace filters by grain rather than
          keeping one "Promotion & Tasks" group: promotion sign-off and task
          state belong to a Type Curve Workspace, verification and ownership to
          an Inventory Workspace — including Created By, which production kept
          under Type Curve Scenario Details. */}
      <FilterSection title="Type Curve Workspace Filters">
        <SelectFilter
          id={LISTING_FILTER_IDS.promotedBy}
          label="Promoted By"
          placeholder="Select user"
          items={promotedByItems}
        />
        <BoolFilter
          id={LISTING_FILTER_IDS.tasksComplete}
          label="Tasks Complete"
          trueLabel="Complete"
          falseLabel="Incomplete"
        />
        {/* The Type Curve serving a (scenario, area, formation) zone by default. Shown on all three
            views, like every other shared-panel filter, and each view answers it in its own terms:
            Type Curves filters its own rows, Workspaces filters the same index down to the version
            holding the flag, and Scenarios resolves it through the detail bridge. Tri-state, so the
            listing shows everything until somebody filters. */}
        <BoolFilter
          id={LISTING_FILTER_IDS.isDefault}
          label="Default"
          trueLabel="Default"
          falseLabel="Not default"
        />
        <SelectFilter
          id={LISTING_FILTER_IDS.validation}
          label="Validation"
          placeholder="Select"
          items={VALIDATION_ITEMS}
        />
      </FilterSection>

      <FilterSection title="Inventory Workspace Filters">
        {/* Every workspace the type curve serves, not only the ones launched
            from it: the server matches on the families each workspace's zones
            resolve to. An exact Unique TC ID, not the public id. */}
        {showTypeCurveId && (
          <TextFilter
            id={LISTING_FILTER_IDS.uniqueTcId}
            label="Type Curve ID"
            placeholder="Unique TC ID"
          />
        )}
        <BoolFilter id={LISTING_FILTER_IDS.promoted} label="Promoted" />
        <SelectFilter
          id={LISTING_FILTER_IDS.verification}
          label="Verification"
          placeholder="Select"
          items={VERIFICATION_ITEMS}
        />
        <SelectFilter
          id={LISTING_FILTER_IDS.createdBy}
          label="Created By"
          placeholder="Select user"
          items={createdByItems}
        />
        <SelectFilter
          id={LISTING_FILTER_IDS.updatedBy}
          label="Updated By"
          placeholder="Select user"
          items={updatedByItems}
        />
      </FilterSection>
    </div>
  );
};
